"use client";

import { useRef, useState } from "react";
import {
  Button,
  Card,
  Typography,
  useLineOverflow,
} from "@cappelendamm/design-system/react";
import Image from "next/image";
import Link from "next/link";
import { OpenBook } from "iconoir-react";
import { LinkPendingLabel } from "@/components/LinkPendingLabel";

export type BookClubCardProps = {
  club: {
    productId?: string | null;
    name: string;
    description?: string | null;
    image?: {
      url?: string | null;
      altText?: string | null;
    } | null;
  };
  className?: string;
};

export function BookClubCard({ club, className }: BookClubCardProps) {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const canExpandDescription = useLineOverflow(
    descriptionRef,
    club.description,
    isDescriptionExpanded,
  );
  const href = club.productId
    ? `/klubb-velger/${club.productId}`
    : "/klubb-velger";

  const isExpanded = isDescriptionExpanded && canExpandDescription;
  const cardHeightClassName = isExpanded ? "md:h-auto" : "md:h-[34rem]";

  return (
    <div
      className={`group block w-full md:col-span-2 ${cardHeightClassName} md:w-[20rem] md:max-w-[20rem] xl:col-span-2 ${className ?? ""}`}
    >
      <Card
        className={`flex ${isExpanded ? "h-auto" : "h-full"} flex-col overflow-hidden p-0 transition group-hover:shadow-lg`}
      >
        {club.image?.url ? (
          <div className="relative aspect-square w-full flex-none overflow-hidden">
            <Image
              src={club.image.url}
              alt={club.image.altText ?? club.name}
              fill
              sizes="(min-width: 768px) 20rem, 100vw"
              className="object-cover"
            />
          </div>
        ) : (
          <div className="flex aspect-square w-full items-center justify-center bg-gray-100 p-4 text-gray-300">
            <OpenBook width={48} height={48} />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-2 bg-white px-6 py-4">
          <Typography
            variant="title-md"
            as="h2"
            className="[overflow-wrap:anywhere] hyphens-auto md:line-clamp-2"
          >
            {club.name}
          </Typography>

          {club.description && (
            <div className="space-y-0.5">
              <Typography
                ref={descriptionRef}
                variant="body-base"
                as="p"
                className={
                  isExpanded ? "text-gray-800" : "line-clamp-2 text-gray-800"
                }
              >
                {club.description}
              </Typography>
              {canExpandDescription && (
                <Button
                  variant="tertiary"
                  as="button"
                  aria-expanded={isExpanded}
                  className="min-h-0! w-fit p-0 text-sm leading-none font-medium underline underline-offset-4"
                  onClick={() =>
                    setIsDescriptionExpanded((current) => !current)
                  }
                >
                  {isExpanded ? "Vis mindre" : "Vis mer"}
                </Button>
              )}
            </div>
          )}

          <div className="mt-auto pt-1">
            <Link href={href} className="block w-full">
              <Button variant="primary" className="relative w-full" as="span">
                <LinkPendingLabel>Se velkomstpakker</LinkPendingLabel>
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
